/**
 * Main Widget Class
 * Orchestrates the live chat widget with Shadow DOM isolation
 */

import type { WidgetConfig, VisitorSession } from './types';
import { WidgetAPI } from './services/api';
import { WidgetWebSocket } from './services/websocket';
import { ChatButton } from './components/ChatButton';
import { PreChatForm } from './components/PreChatForm';
import { ChatWindow } from './components/ChatWindow';
import widgetStyles from './styles/widget.css?inline';

const STORAGE_KEY = 'dokuma_chat_session';

export class DokumaChat {
  private config: WidgetConfig;
  private api: WidgetAPI;
  private ws: WidgetWebSocket | null = null;
  private session: VisitorSession | null = null;

  private shadowRoot: ShadowRoot | null = null;
  private container: HTMLElement | null = null;
  private chatButton: ChatButton | null = null;
  private chatWindow: HTMLElement | null = null;
  private chatWindowComponent: ChatWindow | null = null;
  private preChatForm: PreChatForm | null = null;

  private isOpen = false;

  constructor(config: WidgetConfig) {
    this.config = {
      position: 'right',
      zIndex: 9999,
      primaryColor: '#0f62fe',
      ...config,
    };

    this.api = new WidgetAPI(this.config.apiUrl);
    this.loadSession();
  }

  /**
   * Initialize the widget
   */
  init() {
    console.log('[DokumaChat] Initializing widget');
    
    // Create container with Shadow DOM
    this.container = document.createElement('div');
    this.container.id = 'dokuma-chat-widget';
    document.body.appendChild(this.container);

    // Attach Shadow DOM for complete isolation
    this.shadowRoot = this.container.attachShadow({ mode: 'closed' });

    // Inject styles
    const styleSheet = document.createElement('style');
    styleSheet.textContent = widgetStyles;
    this.shadowRoot.appendChild(styleSheet);

    // Create chat button
    this.chatButton = new ChatButton(
      this.shadowRoot as unknown as HTMLElement,
      () => this.toggle(),
      this.config.position
    );

    console.log('[DokumaChat] Widget initialized');
  }

  /**
   * Open the chat window
   */
  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    // Hide button
    this.chatButton?.hide();

    // Create chat window
    this.createChatWindow();

    // If we have a session, connect to chat
    if (this.session) {
      this.showChatInterface();
    } else {
      this.showPreChatForm();
    }
  }

  /**
   * Close the chat window
   */
  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    // Show button
    this.chatButton?.show();

    // Destroy chat window
    if (this.chatWindow) {
      this.chatWindow.remove();
      this.chatWindow = null;
    }

    // Disconnect WebSocket
    if (this.ws) {
      this.ws.disconnect();
    }

    // Clean up components
    this.chatWindowComponent?.destroy();
    this.preChatForm?.destroy();
  }

  /**
   * Toggle chat window
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Destroy the widget completely
   */
  destroy() {
    this.close();
    this.chatButton?.destroy();
    if (this.container) {
      this.container.remove();
    }
  }

  private createChatWindow() {
    this.chatWindow = document.createElement('div');
    this.chatWindow.className = `chat-window ${this.config.position === 'left' ? 'position-left' : ''}`;
    
    // Header
    const header = document.createElement('div');
    header.className = 'chat-header';
    header.innerHTML = `
      <h3>Chat with us</h3>
      <button class="chat-close" id="chat-close-btn">×</button>
    `;

    this.chatWindow.appendChild(header);

    // Content area
    const content = document.createElement('div');
    content.id = 'chat-content';
    content.style.cssText = 'flex: 1; overflow: hidden; display: flex; flex-direction: column;';
    this.chatWindow.appendChild(content);

    this.shadowRoot?.appendChild(this.chatWindow);

    // Close button handler
    const closeBtn = header.querySelector('#chat-close-btn');
    closeBtn?.addEventListener('click', () => this.close());
  }

  private showPreChatForm() {
    const content = this.chatWindow?.querySelector('#chat-content');
    if (!content) return;

    this.preChatForm = new PreChatForm(
      content as HTMLElement,
      async (data) => {
        try {
          await this.initChat(data.name, data.email, data.message);
          this.preChatForm?.destroy();
          this.showChatInterface();
        } catch (error) {
          console.error('[DokumaChat] Failed to init chat:', error);
          throw error;
        }
      }
    );

    // Fetch and display online status
    this.updateOnlineStatus();
  }

  private async updateOnlineStatus() {
    try {
      const status = await this.api.getOnlineStatus();
      this.preChatForm?.setOnlineStatus(status.online, status.message);
    } catch (error) {
      console.error('[DokumaChat] Failed to get online status:', error);
      // Show as online by default if API fails
      this.preChatForm?.setOnlineStatus(true, 'Online');
    }
  }

  private async initChat(name: string, email: string, message: string) {
    console.log('[DokumaChat] Initializing chat session');

    const session = await this.api.initChat({
      name,
      email,
      initial_message: message || undefined,
      website_url: this.config.websiteUrl || window.location.href,
    });

    this.session = session;
    this.api.setToken(session.token);
    this.saveSession(session);

    console.log('[DokumaChat] Chat session initialized:', session.conversation_id);
  }

  private async showChatInterface() {
    if (!this.session) return;

    const content = this.chatWindow?.querySelector('#chat-content');
    if (!content) return;

    // Create chat window component
    this.chatWindowComponent = new ChatWindow(
      content as HTMLElement,
      this.session.conversation_id,
      async (message) => {
        await this.sendMessage(message);
      },
      (isTyping) => {
        this.sendTypingIndicator(isTyping);
      }
    );

    // Load message history
    await this.loadMessages();

    // Load agent info
    await this.loadAgentInfo();

    // Connect WebSocket
    this.connectWebSocket();
  }

  private async loadMessages() {
    if (!this.session) return;

    try {
      const { messages } = await this.api.getMessages(this.session.conversation_id);
      this.chatWindowComponent?.setMessages(messages);
    } catch (error) {
      console.error('[DokumaChat] Failed to load messages:', error);
    }
  }

  private async loadAgentInfo() {
    if (!this.session) return;

    try {
      const agentInfo = await this.api.getAgentInfo();
      if (agentInfo.agent_name) {
        this.chatWindowComponent?.setAgentInfo(agentInfo.agent_name, agentInfo.agent_online);
      }
    } catch (error) {
      console.error('[DokumaChat] Failed to load agent info:', error);
      // Not critical, just continue without agent info
    }
  }

  private async sendMessage(content: string) {
    if (!this.session) return;

    try {
      const result = await this.api.sendMessage({
        conversation_id: this.session.conversation_id,
        message_type: 'text',
        content,
      });

      // Add message to UI immediately (optimistic update)
      this.chatWindowComponent?.addMessage({
        id: Date.now(), // Temporary ID
        message_id: result.message_id,
        direction: 'inbound',
        message_type: 'text',
        content,
        timestamp: result.timestamp,
        status: 'sent',
      });
    } catch (error) {
      console.error('[DokumaChat] Failed to send message:', error);
      throw error;
    }
  }

  private sendTypingIndicator(isTyping: boolean) {
    if (!this.session) return;
    
    // Send via both API and WebSocket
    this. api.sendTypingIndicator(this.session.conversation_id, isTyping).catch(() => {
      // Fallback to WebSocket only
      this.ws?.sendTyping(isTyping);
    });
  }

  private connectWebSocket() {
    if (!this.session || this.ws) return;

    console.log('[DokumaChat] Connecting to WebSocket');

    this.ws = new WidgetWebSocket(
      this.config.apiUrl,
      this.session.conversation_id,
      this.session.token
    );

    this.ws.onMessage((message) => {
      console.log('[DokumaChat] WS message:', message);

      if (message.type === 'new_message' && message.direction === 'outbound') {
        // Agent sent a message
        this.chatWindowComponent?.addMessage({
          id: Date.now(),
          message_id: message.message_id || '',
          direction: 'outbound',
          message_type: message.message_type as any || 'text',
          content: message.content || '',
          timestamp: message.timestamp || new Date().toISOString(),
          status: 'delivered',
          sender_name: message.sender_name,
        });
        
        // Update agent info in header when agent sends a message
        if (message.sender_name) {
          this.chatWindowComponent?.setAgentInfo(message.sender_name, true);
        }
      } else if (message.type === 'typing') {
        // Agent typing indicator
        const isAgentTyping = message.is_agent === true && message.is_typing === true;
        const agentName = isAgentTyping ? message.agent_name : undefined;
        this.chatWindowComponent?.setAgentTyping(isAgentTyping, agentName);
        
        // Update agent online status if agent starts typing
        if (isAgentTyping && message.agent_name) {
          this.chatWindowComponent?.setAgentInfo(message.agent_name, true);
        }
      }
    });

    this.ws.connect();
  }

  private saveSession(session: VisitorSession) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch (error) {
      console.warn('[DokumaChat] Failed to save session:', error);
    }
  }

  private loadSession() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (data) {
        this.session = JSON.parse(data);
        if (this.session) {
          this.api.setToken(this.session.token);
          console.log('[DokumaChat] Session restored');
        }
      }
    } catch (error) {
      console.warn('[DokumaChat] Failed to load session:', error);
    }
  }
}
